import { DataSourceRequestState } from '@progress/kendo-data-query';
import { UseQueryResult, useQuery } from '@tanstack/react-query';

import { ExamGridModel } from 'models';

import { DataResult } from 'core/api/types/DataResult';
import { useSignalRQueryInvalidation } from 'core/hooks';
import { ColumnState } from 'core/ui';

import { useApiClient } from 'features/api';
import { RelativeDateFilterApiParams } from 'features/worklist/utils/relative-date-filters';

export interface UseExamsGridQueryOptions {
  dataState: DataSourceRequestState | null;
  authMode?: 'msal-required' | 'share-required';
  worklistViewId?: number | null;
  columnsState?: Record<string, ColumnState> | null;
  enabled?: boolean;
  refetchInterval?: number;
  includeFiles?: boolean;
  relativeDateParams?: RelativeDateFilterApiParams;
  searchTrigger?: number; // Used to trigger refetch for search without including searchValue in queryKey

  // SignalR integration options
  enableSignalR?: boolean;
  signalRPatientId?: number;
  signalRExamId?: number;
  signalRLocationId?: number;
  signalRWorklistId?: number;
  signalRDebugLogging?: boolean;
}

export function useExamsGridQuery({
  dataState,
  authMode = 'msal-required',
  worklistViewId,
  columnsState,
  enabled = true,
  refetchInterval,
  includeFiles = false,
  relativeDateParams,
  searchTrigger,
  enableSignalR = true,
  signalRPatientId,
  signalRExamId,
  signalRLocationId,
  signalRWorklistId,
  signalRDebugLogging = false,
}: UseExamsGridQueryOptions): UseQueryResult<DataResult<ExamGridModel>> {
  const apiClient = useApiClient();

  // Automatically integrate SignalR for real-time updates
  useSignalRQueryInvalidation({
    patientId: signalRPatientId,
    examId: signalRExamId,
    locationId: signalRLocationId,
    worklistId: signalRWorklistId,
    enableSmartInvalidation: true,
    enableDebugLogging: signalRDebugLogging,
    debounceMs: 500,

    // Invalidate exam queries on relevant events
    invalidateOnExamChanged: enableSignalR ? ['exams-grid-optimized'] : false,
    invalidateOnPatientChanged: enableSignalR ? ['exams-grid-optimized'] : false,
    invalidateOnFileChanged: enableSignalR ? ['exams-grid-optimized'] : false,
    invalidateOnWorklistChanged: enableSignalR ? ['exams-grid-optimized'] : false,
    invalidateOnSystemDataChanged: enableSignalR ? ['exams-grid-optimized'] : false,
  });

  return useQuery<DataResult<ExamGridModel>>({
    queryKey: [
      'exams-grid-optimized',
      dataState,
      authMode,
      worklistViewId,
      columnsState,
      includeFiles,
      relativeDateParams?.relativeDateField,
      relativeDateParams?.relativeDateType,
    ],
    queryFn: async ({ signal }) => {
      if (!dataState) {
        throw new Error('dataState is required for query execution');
      }

      console.log('useExamsGridQuery - Starting API call with parameters:', {
        dataState,
        authMode,
        worklistViewId,
        includeFiles,
        relativeDateParams,
      });

      try {
        const result = await apiClient.exams.getAllForKendoGridOptimized(
          dataState,
          undefined, // quickFilter - handled in dataState
          undefined, // groupId
          worklistViewId,
          includeFiles,
          relativeDateParams?.relativeDateField,
          relativeDateParams?.relativeDateType,
          authMode,
        );

        console.log('useExamsGridQuery - API call successful, result:', {
          totalCount: result.total,
          dataCount: result.data?.length,
          firstItem: result.data?.[0],
        });

        return result;
      } catch (error) {
        console.error('useExamsGridQuery - API call failed:', error);

        // Log detailed error information
        if (error instanceof Error) {
          console.error('useExamsGridQuery - Error details:', {
            message: error.message,
            stack: error.stack,
            name: error.name,
          });
        }

        // Re-throw to let TanStack Query handle it
        throw error;
      }
    },
    enabled: enabled && dataState != null,
    refetchInterval,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error) => {
      console.log('useExamsGridQuery - Retry attempt:', { failureCount, error });
      return failureCount < 3; // Retry up to 3 times
    },
    meta: {
      errorHandler: (error: Error) => {
        console.error('useExamsGridQuery - Query error handler:', error);
      },
    },
  });
}
